import { useId } from "react";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { springSoft } from "../lib/motion";

export interface SegmentOption<T extends string | number> {
  value: T;
  label: string;
  icon?: LucideIcon;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentOption<T>[];
  /** Gold in admin, crimson on the public quote filters. */
  tone?: "gold" | "crimson";
  size?: "sm" | "md";
  fullWidth?: boolean;
  ariaLabel: string;
  className?: string;
}

/**
 * A sliding pill toggle. The indicator is a single shared layout element so it
 * animates between options instead of cross-fading.
 */
export function SegmentedControl<T extends string | number>({
  value,
  onChange,
  options,
  tone = "gold",
  size = "md",
  fullWidth = false,
  ariaLabel,
  className,
}: SegmentedControlProps<T>) {
  const layoutId = useId();

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-1 rounded-xl border border-line bg-surface-sunken p-1",
        fullWidth && "flex w-full",
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              "relative inline-flex items-center justify-center gap-1.5 rounded-lg font-medium",
              "transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500",
              "disabled:cursor-not-allowed disabled:opacity-50",
              size === "sm" ? "h-7 px-2.5 text-xs" : "h-9 px-3.5 text-sm",
              fullWidth && "flex-1",
              selected
                ? tone === "gold"
                  ? "text-ink-950"
                  : "text-white"
                : "text-content-muted hover:text-content",
            )}
          >
            {selected && (
              <motion.span
                layoutId={layoutId}
                aria-hidden="true"
                transition={springSoft}
                className={cn(
                  "absolute inset-0 rounded-lg shadow-ir-sm",
                  tone === "gold" ? "bg-gold-sheen" : "bg-crimson-sheen",
                )}
              />
            )}
            <span className="relative inline-flex items-center gap-1.5">
              {Icon && <Icon size={size === "sm" ? 13 : 15} aria-hidden="true" />}
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
